import React, { useMemo, useState, useEffect, useContext } from "react"

import { ScreenContext } from "../.."

import qs from "qs"

import userPermissions from "../../../User/Permissions"
import GroupBadge from '../../../Groups/GroupBadge'

import {
    Box,
    Tooltip,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    TextField,
    InputAdornment,
    Link,
} from "@mui/material"

import { useLocation, useHistory } from "react-router-dom"

import LoadingButton from '@mui/lab/LoadingButton'

import {
    DataGridPro,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    useGridApiRef,
    ptBR
} from "@mui/x-data-grid-pro"

import {
    createTheme,
    ThemeProvider
} from "@mui/material/styles"

import { ptBR as corePtBR } from "@mui/material/locale"

import AddCircleIcon from "@mui/icons-material/AddCircle"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import GroupsIcon from "@mui/icons-material/Groups"
import EmailIcon from "@mui/icons-material/Email"
import PhoneIcon from "@mui/icons-material/Phone"
import GroupAddIcon from "@mui/icons-material/GroupAdd"
import EventIcon from "@mui/icons-material/Event"
import SearchIcon from "@mui/icons-material/Search"
import TableRowsIcon from "@mui/icons-material/TableRows"
import SettingsIcon from "@mui/icons-material/Settings"

import { stringOperators, choiceOperators, numericOperators } from "../../../../utils/filterOperators"
import normalizeHttpUrl from '../../../../utils/normalizeHttpUrl'
import { formatCnpj, isValidCnpj } from '../../../../utils/masks'
import { getSession } from "../../../../auth"
import SendSMS from "../../TabButtons/SendMessage/SendSMS"
import ImportEnterprises from "../../TabButtons/ImportEnterprises"
import { GridToolbarImportEnterprises } from "./GridToolbarImportEnterprises"
import { getFilterInputPlaceholderByField } from "../filterPlaceholder"

const hasEmailValidationError = (response) => {
    const payload = getResponsePayload(response)
    const emailErrors = payload?.email || payload?.error?.email || payload?.errors?.email
    if (!emailErrors)
        return false

    if (Array.isArray(emailErrors))
        return emailErrors.some(error => String(error).includes('Enter a valid email address'))

    return String(emailErrors).includes('Enter a valid email address')
}

const parseJson = (value) => {
    try {
        return JSON.parse(value)
    } catch (_) {
        return value
    }
}

const getResponsePayload = (response) => {
    const payload = response?.data ?? response?.response?.data ?? response?.request?.responseText ?? response

    if (typeof payload === 'string')
        return parseJson(payload)

    return payload || {}
}

const getFieldError = (response, field) => {
    const payload = getResponsePayload(response)
    const value = payload.error?.[field] || payload.errors?.[field]

    if (Array.isArray(value))
        return value.join(' ')

    if (value)
        return String(value)

    return ''
}

const getCnpjValidationMessage = (response) => {
    const fieldError = getFieldError(response, 'cnpj')

    if (fieldError)
        return 'Informe um CNPJ válido.'

    const payloadText = JSON.stringify(getResponsePayload(response)).toLowerCase()
    if (payloadText.includes('cnpj') && (payloadText.includes('inválido') || payloadText.includes('invalido') || payloadText.includes('invalid')))
        return 'Informe um CNPJ válido.'

    return ''
}

const notifyCnpjValidationError = (response) => {
    const message = getCnpjValidationMessage(response)

    if (!message)
        return false

    useNotification("CNPJ inválido!", message, "warning")
    return true
}

const hasInvalidCnpj = (cnpj) => Boolean(cnpj) && !isValidCnpj(cnpj)

const showCnpjValidationError = (response) => {
    return notifyCnpjValidationError(response)
}

import EnterpriseGroupButton from "./EnterpriseGroupButton"
import AddEnterpriseGroupForm from "./AddEnterpriseGroupForm"
import EditGroupNameModal from "./EditGroupNameModal"
import EditGroupEnterprisesModal from "./EditGroupEnterprisesModal"

import ContactEnterpriseDialog from "../../../Dialogs/ContactEnterprise"
import BulkEditEnterpriseDialog from "../../../Dialogs/BulkEditEnterprise"
import CustomFieldsDialog from "../../../Dialogs/CustomFields"
import ConfirmationDialog from "../../../Dialogs/Confirmation"
import ConfirmationDialogCaptcha from "../../../Dialogs/ConfimationDialogCaptcha"
import MergeEnterpriseDialog from "../../../Dialogs/MergeEnterprise"
import ContactCard from "../../ContactCard"
import useNotification from "../../../Notification"
import Loading from "../../../Loading"

import Templates from "../../Templates"
import SendEmail from "../../TabButtons/SendMessage/SendEmail"
import Event from "../../../Agenda/Event"

import api from "../../../../api"

import dayjs from 'dayjs'

import "./styles.css"

const userGroups = userPermissions()

const renderBooleanCustomField = (value) => {
    if (value === null || value === undefined || value === '') return ''

    const normalized = String(value).trim().toLowerCase()
    if (['1', 'true', 'sim'].includes(normalized)) return 'Sim'
    if (['0', 'false', 'não', 'nao'].includes(normalized)) return 'Não'
    return ''
}

const nullableChoiceOperators = [...choiceOperators, { value: 'is_empty', label: 'é vazio' }, { value: 'is_not_empty', label: 'não é vazio' }]

const parseFilters = (filterModel, type = "object") => {
    let filterString = filterModel.items.map(each => {
        const columnField = encodeURIComponent(each.columnField)
        if (each.value)
            return (`&${columnField}__${each.operatorValue}=${encodeURIComponent(each.value)}`)
        if (["is_empty", "isEmpty"].includes(each.operatorValue))
            return (`&${columnField}__is_empty=true`)
        if (["is_not_empty", "isNotEmpty"].includes(each.operatorValue))
            return (`&${columnField}__is_not_empty=true`)
    }).join("")
    if ("linkOperator" in filterModel && filterModel.linkOperator == "or")
        filterString += "&or=True"
    if (type == "object") {
        const filters = {}
        const params = new URLSearchParams(filterString.startsWith("&") ? filterString.slice(1) : filterString)
        for (const [key, value] of params.entries()) {
            if (filters.hasOwnProperty(key))
                filters[key] = Array.isArray(filters[key]) ? [...filters[key], value] : [filters[key], value]
            else
                filters[key] = value
        }
        return filters
    }
    return filterString
}

const parseSort = (sortModel, type = "object") => {
    const sortString = sortModel.map(each => (`&sort=${each.field}__${each.sort}`)).join('')
    if (type == "object")
        return Object.fromEntries(new URLSearchParams(sortString.startsWith("&") ? sortString.slice(1) : sortString).entries())
    return sortString
}

function EnterpriseContacts({
    contactEnterpriseId,
    setLoading,
    setProfile,
    setSingleClientWhatsAppNotify,
    setSingleClientEmailNotify,
    setSelectedContact,
    showDeleteContactDialog,
    onContactsLoaded,
    refreshTrigger,
}) {
    const [contacts, setContacts] = useState(null);

    useEffect(() => {
        let mounted = true;

        const loadContacts = async () => {
            try {
                setLoading(true);

                const enterpriseId = getSession().profile?.enterprise?.id;

                const { data } = await api.get(
                    `/api/enterprises/${enterpriseId}/contacts/?contact_enterprise=${contactEnterpriseId}&paginate=false`
                );

                if (mounted) {
                    setContacts(data);
                    onContactsLoaded(data.length);
                }
            } finally {
                if (mounted)
                    setLoading(false);
            }
        };

        loadContacts();

        return () => {
            mounted = false;
        };
    }, [contactEnterpriseId, setLoading, refreshTrigger]);

    if (contacts === null)
        return null;

    return (
        <Box sx={{ backgroundColor: "#f5f5f5", padding: "0 .5rem" }}>
            {(contacts && contacts.length > 0) ? (
                <List sx={{ display: 'flex', flexDirection: "row", flexWrap: "wrap", gap: 1 }}>
                    {contacts.map((contact, index) => (
                        <ContactCard client={contact}
                            onClick={() => setProfile(contact)}
                            onTelephoneClick={() => setSingleClientWhatsAppNotify([contact.id])}
                            onEmailClick={() => setSingleClientEmailNotify(contact.id)}
                            onRemoveClick={() => {
                                setSelectedContact(contact)
                                showDeleteContactDialog(true)
                            }}
                        />
                    ))}
                </List>
            ) : (
                <Typography variant="body2" color="textSecondary" sx={{ padding: 2 }}>
                    Sem contatos.
                </Typography>
            )}
        </Box>
    );
}

export const getEnterpriseColumns = (setSelectedEnterprise, setShowDeleteDialog, setShowContactEnterpriseDialog, updateSelectedTab, getClients, setFilterModel, setShowCreateEvent, setSelectedContactEnterprise, customFields = []) => {

    const location = useLocation()
    const history = useHistory()

    const columns = [
        { field: 'name', headerName: 'Nome', flex: 1, filterOperators: stringOperators },
        { field: 'created_at', headerName: 'Data de Criação', flex: .8, hide: true, filterOperators: stringOperators, valueFormatter: ({ value }) => (value ? dayjs.utc(value).local().format('DD/MM/YYYY HH:mm') : '') },
        { field: 'social_reason', headerName: 'Razão Social', flex: 1, filterOperators: stringOperators },
        { field: 'telephone', headerName: 'Telefone', flex: 1, filterOperators: stringOperators },
        { field: 'email', headerName: 'E-mail', flex: 1, filterOperators: stringOperators },
        { field: 'cnpj', headerName: 'CNPJ', flex: 1, filterOperators: stringOperators,
          valueFormatter: ({ value }) => formatCnpj(value) },
        {
            field: 'linkedin_url',
            headerName: 'LinkedIn',
            flex: 1,
            hide: true,
            filterOperators: stringOperators,
            renderCell: (params) => {
                const value = params.row.linkedin_url;
                const href = normalizeHttpUrl(value);

                if (!href) return '';

                return (
                    <Link
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {value}
                    </Link>
                );
            }
        },
        {
            field: 'groups', headerName: 'Segmentos', flex: 1, sortable: false, filterOperators: stringOperators,
            valueGetter: ({ value }) => ((value) ? value.map(each => each.name).join() : ""),
            renderCell: (params) => (
                params.row.groups
                    ? <>
                        {params.row.groups.map((group) => (
                            <GroupBadge onClick={() => setFilterModel({
                                items: [{
                                    columnField: "groups",
                                    operatorValue: "contains",
                                    value: String(group.name)
                                }],
                            })} className='client-group-item pointer' label={group.name} isEnterprise={true} />
                        ))}
                    </>
                    : <></>
            )
        },

        { field: 'address', headerName: 'Endereço', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'address_number', headerName: 'Número', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'address_extra', headerName: 'Complemento', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'neighborhood', headerName: 'Bairro', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'city', headerName: 'Cidade', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'estate', headerName: 'Estado', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'postal_code', headerName: 'CEP', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'code', headerName: 'Código', flex: 1, hide: true, filterOperators: stringOperators },
        { field: 'field', headerName: 'Área de Atuação', flex: 1, hide: true, filterOperators: stringOperators },
    ]
    
    for (const custom of customFields) {
        const column = {
            field: `custom_${custom.field_name}`,
            headerName: custom.field_name,
            hide: false,
            flex: 1,
            sortable: false,
            renderCell: (params) => {
                if (params.row.custom_fields_data && Object.prototype.hasOwnProperty.call(params.row.custom_fields_data, custom.id)) {
                    const value = params.row.custom_fields_data[custom.id]
                    if (custom.field_type === 'boolean') {
                        return renderBooleanCustomField(value)
                    }
                    return value
                }
                return ''
            }
        }
        if (custom.field_type === 'integer') {
            column.filterOperators = numericOperators
        }
        if (custom.field_type === 'boolean') {
            column.filterOperators = nullableChoiceOperators
            column.valueOptions = ['Sim', 'Não']
        }
        columns.push(column)
    }
    
    columns.push({
            field: 'action', headerName: 'Ações', flex: .75, sortable: false, filterable: false,
            renderCell: (params) => (
                <Box>
                    {getSession().profile.enterprise.show_agenda &&
                        <Tooltip title="Criar Evento" interactive>
                            <Button className="action-button"
                                onClick={() => { setShowCreateEvent(true); setSelectedContactEnterprise(params.row) }}>
                                <EventIcon />
                            </Button>
                        </Tooltip>
                    }
                    <Tooltip title="Ver Contatos" interactive>
                        <Button className="action-button" onClick={() => {
                            let currentFilters = JSON.parse(localStorage.getItem('crmFilters'))
                            if (!currentFilters)
                                currentFilters = {
                                    "campaigns": { "items": [] },
                                    "clients": { "items": [] },
                                    "notifications": { "items": [] }
                                }
                            currentFilters["clients"]["items"] = [{
                                columnField: "contact_enterprise_name",
                                operatorValue: "equals",
                                value: params.row.name
                            }]
                            localStorage.setItem("crmFilters", JSON.stringify(currentFilters))
                            const urlParams = new URLSearchParams()
                            urlParams.set("selectedTab", "contacts")
                            history.push(`${location.pathname}?${urlParams.toString()}`)
                        }}>
                            <GroupsIcon />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Editar" interactive>
                        <Button className="action-button" onClick={() => { setSelectedEnterprise(params.row); setShowContactEnterpriseDialog(true); }}>
                            <EditIcon />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Excluir" interactive>
                        <Button className="action-button delete" onClick={() => { setSelectedEnterprise(params.row); setShowDeleteDialog(true); }}>
                            <DeleteIcon />
                        </Button>
                    </Tooltip>
                </Box>
            )
        })
    
    return columns
}

const CustomToolbar = (setSelectedEnterprise, setShowContactEnterpriseDialog, setShowMassDeleteEnterpriseDialog, selectedEnterprises, checkShowMergeDialog, setShowAddEnterpriseGroup, setShowDeleteEnterpriseGroup, setSelectedEnterpriseGroup, setShowUpdateEnterpriseGroup, addSelectedEnterprisesToGroup, setFilterModel, setShowGroupEnterprises, loadMoreGroups, setShowCustomFields, setShowMassBulkEditEnterpriseDialog) => {
    return (
        <GridToolbarContainer>
            <Box>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarImportEnterprises startIcon={<AddCircleIcon />}>
                    <ListItem className={'menuItem'} sx={{ padding: 0 }}>
                        <Box sx={{ width: "100%", padding: ".5rem", marginRight: "1.5rem" }} onClick={() => {
                            setSelectedEnterprise(null)
                            setShowContactEnterpriseDialog(true)
                        }}>Nova empresa</Box>
                    </ListItem>
                    <ImportEnterprises />
                </GridToolbarImportEnterprises>
                <EnterpriseGroupButton
                    addEnterpriseGroup={() => setShowAddEnterpriseGroup(true)}
                    deleteEnterpriseGroupModal={(value) => {
                        setSelectedEnterpriseGroup(value)
                        setShowDeleteEnterpriseGroup(true)
                    }}
                    editEnterpriseGroupModal={(value) => {
                        setSelectedEnterpriseGroup(value)
                        setShowUpdateEnterpriseGroup(true)
                    }}
                    selectedEnterpriseGroup={(value) => {
                        setSelectedEnterpriseGroup(value)
                        setShowGroupEnterprises(true)
                    }}
                    filterOnClick={(groupName) => {
                        setFilterModel({
                            items: [{
                                columnField: "groups",
                                operatorValue: "contains",
                                value: String(groupName)
                            }],
                        })
                    }}
                    selectedEnterprises={selectedEnterprises}
                    addSelectedEnterprisesToGroup={addSelectedEnterprisesToGroup}
                    loadMoreGroups={loadMoreGroups}
                />
                <LoadingButton component="label" startIcon={<GroupAddIcon />} onClick={() => {
                    checkShowMergeDialog()
                }} size="small">Mesclar</LoadingButton>
                <LoadingButton component="label" startIcon={<DeleteIcon />} onClick={() => {
                    if (selectedEnterprises.length === 0) {
                        useNotification(
                            "Atenção!",
                            "Seleciona uma ou mais empresas para excluir.",
                            "warning"
                        )
                        return
                    }
                    setShowMassDeleteEnterpriseDialog(true)
                }} size="small">Excluir</LoadingButton>
                {selectedEnterprises.length > 1 &&
                    <LoadingButton component="label" startIcon={<EditIcon />} onClick={() => {
                        setShowMassBulkEditEnterpriseDialog(true)
                    }} size="small">Editar selecionados</LoadingButton>
                }
            </Box>
            <Box className='toolbarRight'>
                {userGroups.isAdmin &&
                    <LoadingButton
                        component="label"
                        startIcon={<TableRowsIcon />}
                        onClick={() => setShowCustomFields(true)}
                        size="small"
                    >
                        Campos Customizados
                    </LoadingButton>
                }
            </Box>
        </GridToolbarContainer>
    )
}

const theme = createTheme({
    palette: {
        primary: { main: "#21446C" },
    },
}, ptBR, corePtBR);

const GRID_LAYOUT_KEY = 'crmEnterpriseGridLayout_v1'

const optionalColumnSizes = {
    linkedin_url: { minWidth: 200 },
    address: { minWidth: 220 },
    address_number: { minWidth: 110 },
    address_extra: { minWidth: 180 },
    neighborhood: { minWidth: 160 },
    city: { minWidth: 150 },
    estate: { minWidth: 100 },
    postal_code: { minWidth: 120 },
    code: { minWidth: 140 },
    field: { minWidth: 180 },
}

const defaultColumnVisibilityModel = {
    created_at: false,
    linkedin_url: false,
    address: false,
    address_number: false,
    address_extra: false,
    neighborhood: false,
    city: false,
    estate: false,
    postal_code: false,
    code: false,
    field: false,
}

const getEmptyGridLayout = () => ({
    order: [],
    widths: {},
    visibility: {},
})

const getGridLayout = () => {
    try {
        const savedLayout = JSON.parse(localStorage.getItem(GRID_LAYOUT_KEY))

        if (savedLayout) {
            return {
                ...getEmptyGridLayout(),
                ...savedLayout,
                widths: savedLayout.widths || {},
                visibility: savedLayout.visibility || {},
                order: savedLayout.order || [],
            }
        }
    } catch (error) {
        return getEmptyGridLayout()
    }

    return getEmptyGridLayout()
}

const saveGridLayout = (partialLayout) => {
    const currentLayout = getGridLayout()

    const nextLayout = {
        ...currentLayout,
        ...partialLayout,
        widths: {
            ...currentLayout.widths,
            ...(partialLayout.widths || {}),
        },
        visibility: {
            ...currentLayout.visibility,
            ...(partialLayout.visibility || {}),
        },
    }

    localStorage.setItem(GRID_LAYOUT_KEY, JSON.stringify(nextLayout))
}

const getInitialColumnVisibilityModel = () => ({
    ...defaultColumnVisibilityModel,
    ...getGridLayout().visibility,
})

const applyGridLayoutToColumns = (baseColumns) => {
    const layout = getGridLayout()
    const columnsMap = new Map(baseColumns.map((column) => [column.field, { ...column }]))
    const orderedFields = []

    layout.order.forEach((field) => {
        if (columnsMap.has(field))
            orderedFields.push(field)
    })

    baseColumns.forEach((column) => {
        if (!orderedFields.includes(column.field))
            orderedFields.push(column.field)
    })

    return orderedFields.map((field) => {
        const column = {
            ...optionalColumnSizes[field],
            ...columnsMap.get(field),
        }

        if (field in layout.visibility)
            column.hide = !layout.visibility[field]

        if (field in layout.widths) {
            column.width = layout.widths[field]
            delete column.flex
        }

        return column
    })
}

const EnterpriseTable = ({ updateSelectedTab, getClients, loadMoreGroups }) => {

    const { subsObj, updateGroups, rmvObj, groups, setProfile, templates, queues, clients, originNumbers, profile, myTeam } = useContext(ScreenContext)

    const [selectedEnterprise, setSelectedEnterprise] = useState()
    const [showContactEnterpriseDialog, setShowContactEnterpriseDialog] = useState(false)
    const [showDeleteDialog, setShowDeleteDialog] = useState(false)
    const [showMassDeleteEnterpriseDialog, setShowMassDeleteEnterpriseDialog] = useState(false)
    const [showMassBulkEditEnterpriseDialog, setShowMassBulkEditEnterpriseDialog] = useState(false)
    const [selectedEnterprises, updateSelectedEnterprises] = useState([])
    const [enterprises, setEnterprises] = useState([])
    const [contactsRefreshTrigger, setContactsRefreshTrigger] = useState(0)
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(25)
    const [rowCount, setRowCount] = useState(0)
    const [sortModel, setSortModel] = useState([])
    const [filterModel, setFilterModel] = useState((JSON.parse(localStorage.getItem('crmEnterpriseFilters'))) ? JSON.parse(localStorage.getItem('crmEnterpriseFilters')) : { items: [] })
    const [loading, setLoading] = useState(false)
    const [showMergeDialog, setShowMergeDialog] = useState(false)
    const [showAddEnterpriseGroup, setShowAddEnterpriseGroup] = useState(false)
    const [showDeleteEnterpriseGroup, setShowDeleteEnterpriseGroup] = useState(false)
    const [selectedEnterpriseGroup, setSelectedEnterpriseGroup] = useState([])
    const [showUpdateEnterpriseGroup, setShowUpdateEnterpriseGroup] = useState(false)
    const [showGroupEnterprises, setShowGroupEnterprises] = useState(false)
    const [selectedContact, setSelectedContact] = useState()
    const [deleteContactDialog, showDeleteContactDialog] = useState(false)
    const [singleClientWhatsAppNotify, setSingleClientWhatsAppNotify] = useState(null)
    const [singleClientSMSNotify, setSingleClientSMSNotify] = useState(null)
    const [singleClientEmailNotify, setSingleClientEmailNotify] = useState(null)
    const [showCreateEvent, setShowCreateEvent] = useState(false)
    const [selectedContactEnterprise, setSelectedContactEnterprise] = useState(null)
    const [setup, setSetup] = useState(null)
    const [fastFilter, setFastFilter] = useState(null)
    const [debouncedValue, setDebouncedValue] = useState(null)
    const [filterPlaceholderField, setFilterPlaceholderField] = useState("name")
    const [showCustomFields, setShowCustomFields] = useState(false)
    const [customFields, setCustomFields] = useState([])
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(getInitialColumnVisibilityModel)
    const [detailPanelHeights, setDetailPanelHeights] = useState({});

    const apiRef = useGridApiRef()

    const updateEnterpriseFilterModel = (newFilterModel) => {
        setPage(0)
        setFilterModel(newFilterModel)
    }

    const handlePageSizeChange = (newPageSize) => {
        const firstRowIndex = page * pageSize
        setPage(Math.floor(firstRowIndex / newPageSize))
        setPageSize(newPageSize)
    }

    const checkShowMergeDialog = () => {
        if (selectedEnterprises.length !== 2) {
            useNotification(
                'Atenção!',
                'Selecione 2 empresas na tabela para mesclar!',
                'warning'
            );
            return
        }
        setShowMergeDialog(true)
    }

    const deleteEnterprises = async (ids) => {
        const response = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/bulk_delete/`, {
            ids: ids
        })
        if (response.status === 204) {
            setEnterprises((prevEnterprises) => prevEnterprises.filter(enterprise => !ids.includes(enterprise.id)))
            setShowMassDeleteEnterpriseDialog(false)
            useNotification(
                "Sucesso!",
                "Empresas excluídas com sucesso.",
                "success"
            )
            return
        }

        useNotification(
            "Atenção!",
            "Uma ou mais empresas estão relacionadas com contatos, portanto não podem ser excluídas.",
            "warning"
        )
    }

    const bulkUpdateEnterprises = async ({ customFieldsData, selectedGroups }) => {
        const response = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/bulk_update/`, {
            ids: selectedEnterprises,
            contact_groups: selectedGroups || [],
            custom_fields: customFieldsData || {}
        })

        if (response.status === 200) {
            setShowMassBulkEditEnterpriseDialog(false)
            updateSelectedEnterprises([])
            await fetchEnterprises()
            useNotification(
                "Sucesso!",
                "Empresas atualizadas com sucesso.",
                "success"
            )
            return
        }

        useNotification(
            "Ops!",
            "Algo deu errado, tente novamente.",
            "danger"
        )
    }

    const deleteEnterprise = async (id) => {
        const response = await api.delete(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${id}/`)
        if (response.status === 204) {
            setEnterprises((prevEnterprises) => prevEnterprises.filter(enterprise => enterprise.id !== id))
            setShowDeleteDialog(false)
            useNotification(
                "Sucesso!",
                "Empresa excluída com sucesso.",
                "success"
            )
            return
        }

        useNotification(
            "Atenção!",
            "Esta empresa está relacionada com um ou mais contatos, portanto não pode ser excluída.",
            "warning"
        )
    }

    const createEnterprise = async (data) => {

        const contactGroups = data.selectedGroups
        const customFieldsData = data.customFieldsData || {}

        // Parent is sending the full state
        delete data.groupsSelectOpen
        delete data.contactGroups
        delete data.groupsLoading
        delete data.selectedGroups
        delete data.nextGroupsPointer
        delete data.customFieldsData

        data["contactGroups"] = contactGroups

        data = Object.keys(data).reduce((acc, key) => {
            const snakeKey = key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase()
            acc[snakeKey] = data[key]
            return acc
        }, {})

        if (hasInvalidCnpj(data.cnpj)) {
            useNotification("Atenção!", "Informe um CNPJ válido.", "warning")
            return
        }

        if (data.domain === '')
            delete data.domain

        const response = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/`, data)

        if (notifyCnpjValidationError(response))
            return

        if (response.status === 201) {
            const contactEnterpriseId = response.data.id

            const promises = []
            for (const [fieldId, value] of Object.entries(customFieldsData)) {
                promises.push(api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${contactEnterpriseId}/custom_fields/`, {
                    custom_field: fieldId,
                    value: String(value)
                }))
            }
            
            const responses = await Promise.allSettled(promises)

            const enterprise_custom_fields = response.data.custom_fields || []
            responses.flatMap(response => {
                if (response.status !== 'fulfilled') return []
                if (![200, 201].includes(response.value.status)) return []
                enterprise_custom_fields.push(response.value.data)
            })

            const custom_fields_data = Object.fromEntries(enterprise_custom_fields.map(field => ([field.custom_field, field.value])))

            const new_data = { ...response.data, 
                custom_fields_data: custom_fields_data }
            
            setEnterprises((prevEnterprises) => [
                ...prevEnterprises,
                new_data,
            ])
            setShowContactEnterpriseDialog(false)
            useNotification(
                "Sucesso!",
                `Empresa criada com sucesso.`,
                "success"
            )
            return
        } else if (response.status === 400 ) {
            if (hasEmailValidationError(response)) {
                useNotification("Atenção!", "Informe um e-mail válido.", "warning")
                return
            }
            if (showCnpjValidationError(response))
                return

            const errors = response?.data?.non_field_errors ?? []
            if (errors.length) {
                if (errors.some(e => e.includes("enterprise, name"))) {
                    useNotification("Atenção!", "Já existe uma empresa com esse nome.", "warning")
                    return
                }
                if (errors.some(e => e.includes("enterprise, domain"))) {
                    useNotification("Atenção!", "Este domínio já está em uso.", "warning")
                    return
                }
            }
        }

        if (showCnpjValidationError(response))
            return

        useNotification(
            "Ops!",
            `Algo deu errado, tente novamente.`,
            "danger"
        )
    }

    const updateEnterprise = async (data) => {

        const contactGroups = data.selectedGroups
        const customFieldsData = data.customFieldsData || {}

        // Parent is sending the full state
        delete data.groupsSelectOpen
        delete data.contactGroups
        delete data.groupsLoading
        delete data.selectedGroups
        delete data.nextGroupsPointer
        delete data.customFieldsData

        data["contactGroups"] = contactGroups

        data = Object.keys(data).reduce((acc, key) => {
            const snakeKey = key.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase()
            acc[snakeKey] = data[key]
            return acc
        }, {})

        if (hasInvalidCnpj(data.cnpj)) {
            useNotification("Atenção!", "Informe um CNPJ válido.", "warning")
            return
        }

        if (data.domain === '')
            data.domain = null

        const response = await api.patch(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${data.id}/`, data)

        if (notifyCnpjValidationError(response))
            return

        if (response.status === 200) {
            const contactEnterpriseId = data.id
            
            const custom_fields_dict = Object.fromEntries(response.data.custom_fields.map(field => ([field.custom_field, field.id])))
            const promises = []
            for (const [fieldId, value] of Object.entries(customFieldsData)) {
                const realId = custom_fields_dict[fieldId]
                if (realId)
                    promises.push(api.patch(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${contactEnterpriseId}/custom_fields/${realId}/`, {
                        value: String(value)
                    }))
                else
                    promises.push(api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${contactEnterpriseId}/custom_fields/`, {
                        custom_field: fieldId,
                        value: String(value)
                    }))
            }
            
            const responses = await Promise.allSettled(promises)

            const enterprise_custom_fields = response.data.custom_fields || []
            responses.flatMap(response => {
                if (response.status !== 'fulfilled') return []
                if (![200, 201].includes(response.value.status)) return []
                enterprise_custom_fields.push(response.value.data)
            })

            const custom_fields_data = Object.fromEntries(enterprise_custom_fields.map(field => ([field.custom_field, field.value])))

            const new_data = { ...response.data, 
                custom_fields_data: custom_fields_data }

            setEnterprises((prevEnterprises) =>
                prevEnterprises.map((enterprise) =>
                    enterprise.id === data.id ? {...enterprise, ...new_data} : enterprise
                )
            )
            setShowContactEnterpriseDialog(false)
            useNotification(
                "Sucesso!",
                `Empresa atualizada com sucesso.`,
                "success"
            )
            return
        }

        if (response.status === 400) {
            if (hasEmailValidationError(response)) {
                useNotification("Atenção!", "Informe um e-mail válido.", "warning")
                return
            }
            if (showCnpjValidationError(response))
                return

            const message = JSON.stringify(response.data)
            const error_selector = message === '{"non_field_errors":["The fields enterprise, name must make a unique set."]}' ? 'nome': 'domínio'
            useNotification(
                "Atenção!",
                `Já existe uma empresa com esse ${error_selector}.`,
                "warning"
            )
            return
        }

        if (showCnpjValidationError(response))
            return

        useNotification(
            "Ops!",
            "Algo deu errado, tente novamente.",
            "danger"
        )
    }

    const columns = applyGridLayoutToColumns(
        getEnterpriseColumns(setSelectedEnterprise, setShowDeleteDialog, setShowContactEnterpriseDialog, updateSelectedTab, getClients, updateEnterpriseFilterModel, setShowCreateEvent, setSelectedContactEnterprise, customFields)
    )

    const fetchEnterprises = async () => {
        setLoading(true)
        const response = await api.get(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/paginated/`, {
            params: {
                ...{ page: page + 1, pageSize },
                ...parseFilters(filterModel),
                ...parseSort(sortModel)
            },
            paramsSerializer: params => qs.stringify(params, { arrayFormat: "repeat" })
        })
        setLoading(false)
        if (response.status === 200) {
            setRowCount(response.data.count)
            

            const enterprisesWithCustomFields = (
                response.data.results.map((enterprise) => {

                    const customFieldsData = {}
                    enterprise.custom_fields.forEach(field => {
                        customFieldsData[field.custom_field] = field.value
                    })
                    return {
                        ...enterprise,
                        custom_fields_data: customFieldsData
                    }
                })
            )
            
            setEnterprises(enterprisesWithCustomFields)
            return
        }

        useNotification(
            "Ops!",
            "Algo deu errado ao buscar as empresas.",
            "danger"
        )
    }


    const mergeSelectedEnterprises = async (enterprise1, enterprise2, finalEnterprise, finalCustomFields) => {
        const update = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/relocate_contacts/`, {
            primary_id: finalEnterprise.id,
            secondary_id: (enterprise1.id === finalEnterprise.id) ? enterprise2.id : enterprise1.id
        })
        if (update.status !== 200) {
            useNotification(
                "Ops!",
                "Algo deu errado, tente novamente...",
                "danger"
            )
            return
        }

        // Remove secondary contact enterprise
        const toRemove = (enterprise1.id === finalEnterprise.id) ? enterprise2 : enterprise1
        const delete_ = await api.delete(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${toRemove.id}/`)
        if (delete_.status !== 204) {
            useNotification(
                "Ops!",
                "Algo deu errado, tente novamente...",
                "danger"
            )
            return
        }
        setEnterprises((prevEnterprises) => prevEnterprises.filter(enterprise => enterprise.id !== toRemove.id))

        const patchData = {
            ...finalEnterprise,
            contact_groups: finalEnterprise.groups || []
        }
        const patch = await api.patch(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${finalEnterprise.id}/`, patchData)
        if (![200, 205].includes(patch.status)) {
            useNotification(
                "Ops!",
                "Algo deu errado, tente novamente...",
                "danger"
            )
            return
        }
        setEnterprises((prevEnterprises) => prevEnterprises.map((enterprise) => enterprise.id === finalEnterprise.id ? { ...enterprise, ...patch.data } : enterprise))

        if (finalCustomFields) {
            const contactEnterpriseId = finalEnterprise.id
            
            const existingFields = await api.get(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${contactEnterpriseId}/custom_fields/`)
            
            if (existingFields.status === 200) {
                for (const field of existingFields.data) {
                    await api.delete(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${contactEnterpriseId}/custom_fields/${field.id}/`)
                }
            }
            
            for (const [fieldId, value] of Object.entries(finalCustomFields)) {
                if (value !== null && value !== undefined && value !== '') {
                    await api.post(`api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${contactEnterpriseId}/custom_fields/`, {
                        custom_field: fieldId,
                        value: String(value)
                    })
                }
            }
        }

        setShowMergeDialog(false)

        useNotification(
            "Sucesso!",
            "Empresas mescladas com sucesso!",
            "success"
        );
    }

    const getCustomFields = async () => {
        const response = await api.get(`api/enterprises/${getSession().profile.enterprise.id}/enterprise_custom_fields/`)
        if (response.status === 200) {
            setCustomFields(response.data)
        }
    }

    const submitCustomFields = async (toUpdateFields, toCreateFields, toDeleteFields) => {
        for (const fieldId of toDeleteFields) {
            await api.delete(`api/enterprises/${getSession().profile.enterprise.id}/enterprise_custom_fields/${fieldId}/`)
        }

        for (const field of toUpdateFields) {
            await api.patch(`api/enterprises/${getSession().profile.enterprise.id}/enterprise_custom_fields/${field.id}/`, field)
        }

        for (const field of toCreateFields) {
            await api.post(`api/enterprises/${getSession().profile.enterprise.id}/enterprise_custom_fields/`, field)
        }

        setShowCustomFields(false)
        useNotification(
            'Sucesso!',
            'Campos customizados atualizados com sucesso!',
            'success'
        );
        await getCustomFields()
        await fetchEnterprises()
    }

    const deleteEnterpriseGroup = async () => {
        const response = await api.delete(`/api/enterprises/${getSession().profile.enterprise.id}/contact_group/${selectedEnterpriseGroup.id}/`)
        if (response.status == 403) {
            useNotification(
                "Ops!",
                "Você não tem permissão para excluir este segmento.",
                "danger"
            );
            return
        }

        updateGroups((prevState) => rmvObj(prevState, selectedEnterpriseGroup.id))
        setEnterprises((prevState) => prevState.map(each => {
            each.groups = each.groups.filter(group => group.id !== selectedEnterpriseGroup.id)
            return each
        }))

        useNotification(
            "Sucesso!",
            "Segmento excluído com sucesso!",
            "success"
        );
        setShowDeleteEnterpriseGroup(false)
    }

    const addSelectedEnterprisesToGroup = async (selectedGroup) => {
        if (selectedEnterprises.length === 0) {
            useNotification(
                "Atenção!",
                "Selecione ao menos um empresa.",
                "warning"
            )
            return
        }
        const response = await api.post(`/api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/add_to_group/`, {
            group_id: selectedGroup.id,
            contact_enterprises: selectedEnterprises
        })
        if (response.status === 201) {
            setEnterprises((prevState) => prevState.map(each => {
                if (selectedEnterprises.includes(each.id)) {
                    const existingGroups = each.groups || []
                    if (!existingGroups.some(group => group.id === selectedGroup.id))
                        return { ...each, groups: [...existingGroups, selectedGroup] }
                }
                return each
            }))
            useNotification(
                "Sucesso!",
                "Empresas adicionados ao segmento com sucesso!",
                "success"
            )
            return
        }
        useNotification(
            "Ops!",
            "Algo deu errado, tente novamente!",
            "danger"
        )
    }

    useEffect(() => {
        fetchEnterprises()
        getCustomFields()
        window.fetchEnterprises = fetchEnterprises
        return () => {
            window.fetchEnterprises = () => { }
        }
    }, [page, pageSize, sortModel, filterModel])

    useEffect(() => {
        setColumnVisibilityModel(
            Object.fromEntries(columns.map(column => {
                return ([column.field, !column.hide])
            }))
        )
    }, [customFields])

    useEffect(() => {
        const handleColumnOrderChange = (params) => {
            const fields = apiRef.current.getAllColumns().map((column) => column.field)
            const oldIndex = params.oldIndex - 1
            const targetIndex = params.targetIndex - 1

            if (
                oldIndex < 0 ||
                targetIndex < 0 ||
                oldIndex >= fields.length ||
                targetIndex >= fields.length
            )
                return

            const movedField = fields[oldIndex]
            fields.splice(oldIndex, 1)
            fields.splice(targetIndex, 0, movedField)

            saveGridLayout({ order: fields })
        }

        let unsubscribe
        if (typeof apiRef.current?.subscribeEvent === "function")
            unsubscribe = apiRef.current.subscribeEvent("columnOrderChange", handleColumnOrderChange)

        return () => {
            if (typeof unsubscribe === "function")
                unsubscribe()
        }
    }, [apiRef, customFields])

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const selectedEnterprise = params.get("selectedEnterprise")
        
        if (!selectedEnterprise) return;

        api.get(`/api/enterprises/${getSession().profile.enterprise.id}/contact_enterprises/${selectedEnterprise}/`).then((res) => {
            if (res.status === 200) {
                setSelectedEnterprise(res.data);
                setShowContactEnterpriseDialog(true);
            }
        });
    }, []);

    const table = useMemo(() => {

        return (
            <Box sx={{ width: "100%", height: "100%" }}>
                {loading ? <Loading loading={true} /> : null}
                <ThemeProvider theme={theme}>
                    <DataGridPro
                        apiRef={apiRef}
                        columns={columns}
                        rows={enterprises}
                        components={{ Toolbar: () => CustomToolbar(setSelectedEnterprise, setShowContactEnterpriseDialog, setShowMassDeleteEnterpriseDialog, selectedEnterprises, checkShowMergeDialog, setShowAddEnterpriseGroup, setShowDeleteEnterpriseGroup, setSelectedEnterpriseGroup, setShowUpdateEnterpriseGroup, addSelectedEnterprisesToGroup, updateEnterpriseFilterModel, setShowGroupEnterprises, loadMoreGroups, setShowCustomFields, setShowMassBulkEditEnterpriseDialog) }}
                        density={"compact"}
                        checkboxSelection
                        disableSelectionOnClick
                        selectionModel={selectedEnterprises}
                        onSelectionModelChange={(newSelection) => updateSelectedEnterprises(newSelection)}
                        rowCount={rowCount}
                        pagination
                        paginationMode="server"
                        sortingMode="server"
                        filterMode="server"
                        page={page}
                        pageSize={pageSize}
                        onPageChange={(newPage) => setPage(newPage)}
                        onPageSizeChange={handlePageSizeChange}
                        sortModel={sortModel}
                        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                        filterModel={filterModel}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(newModel) => {
                            setColumnVisibilityModel(newModel)
                            saveGridLayout({ visibility: newModel })
                        }}
                        onColumnVisibilityChange={(updatedColumn) => {
                            saveGridLayout({
                                visibility: {
                                    [updatedColumn.field]: updatedColumn.isVisible
                                }
                            })
                        }}
                        onColumnWidthChange={(params) => {
                            saveGridLayout({
                                widths: {
                                    [params.colDef.field]: params.width
                                }
                            })
                        }}
                        onFilterModelChange={(newFilterModel) => {
                            const currentFilter = (newFilterModel.items || []).find((item) => item?.columnField || item?.field)
                            const currentField = currentFilter?.columnField || currentFilter?.field || "name"
                            const previousFilter = (filterModel.items || []).find((item) => item?.columnField || item?.field)
                            const previousField = previousFilter?.columnField || previousFilter?.field || "name"
                            if (currentField !== previousField)
                                setFilterPlaceholderField(currentField)
                            localStorage.setItem('crmEnterpriseFilters', JSON.stringify(newFilterModel))
                            updateEnterpriseFilterModel(newFilterModel)
                        }}
                        getDetailPanelContent={({ row }) => (
                            <EnterpriseContacts
                                contactEnterpriseId={row.id}
                                setLoading={setLoading}
                                setProfile={setProfile}
                                setSingleClientWhatsAppNotify={setSingleClientWhatsAppNotify}
                                setSingleClientEmailNotify={setSingleClientEmailNotify}
                                setSelectedContact={setSelectedContact}
                                showDeleteContactDialog={showDeleteContactDialog}
                                onContactsLoaded={(count) => {
                                    const data = (count / window.innerWidth * 1000) / 3.125;
                                    const height = (data > 5 ? 5 : count ? Math.ceil(data) : 0.5) * 176;
                                    setDetailPanelHeights(prev => ({
                                        ...prev,
                                        [row.id]: height,
                                    }));
                                }}
                                refreshTrigger={contactsRefreshTrigger}
                            />
                        )}
                        getDetailPanelHeight={({ row }) =>
                            detailPanelHeights[row.id] ?? 200
                        }
                    />
                </ThemeProvider>
            </Box>)
    }, [enterprises, selectedEnterprises, showDeleteContactDialog, loading, profile, columnVisibilityModel, contactsRefreshTrigger])

    const deleteContact = async (contact) => {
        const response = await api.delete(`/api/enterprises/${getSession().profile.enterprise.id}/contacts/${contact.id}/`)
        if (response.status == 204) {
            useNotification(
                "Sucesso!",
                "Cliente removido com sucesso",
                "success"
            )
            setEnterprises((prevEnterprises) =>
                prevEnterprises.map((enterprise) => {
                    if (contact && enterprise.id === contact.contact_enterprise) {
                        return {
                            ...enterprise,
                            contacts: enterprise.contacts ? enterprise.contacts.filter(c => c.id !== contact.id) : [],
                        }
                    }
                    return enterprise
                })
            )
            setContactsRefreshTrigger((prev) => prev + 1)
            return
        }
        useNotification(
            "Ops!",
            "Algo deu errado, tente novamente",
            "danger"
        )
    }

    const createEvent = async (data) => {
        const response = await api.post(`api/enterprises/${getSession().profile.enterprise.id}/tasks/`, {
            ...data,
            target_date: dayjs(data.target_date).format("YYYY-MM-DDTHH:mm"),
            contact: (data.contact) ? data.contact.id : null,
            contact_enterprise: (data.contact_enterprise) ? data.contact_enterprise.id : null,
            owner: getSession().id,
            status: "SCHEDULED",
            target: (!data.target) ? getSession().id : data.target,
            is_recurrent: (data.is_recurrent === "UNIQUE") ? "" : data.is_recurrent
        })
        if (response.status === 201) {
            setShowCreateEvent(false)
            setSelectedEnterprise(null)
            await useNotification(
                "Sucesso",
                "Tarefa criada com sucesso.",
                "success"
            )
            return
        }
        await useNotification(
            "Ops",
            "Algo deu errado, tente novamente.",
            "danger"
        )
    }

    useEffect(() => {
        if (fastFilter !== null) {
            const handler = setTimeout(() => {
                setDebouncedValue(fastFilter)
            }, 1000)
            return () => clearTimeout(handler)
        }
    }, [fastFilter])

    useEffect(() => {
        if (debouncedValue !== null) {
            updateEnterpriseFilterModel({
                items: [{
                    columnField: filterPlaceholderField,
                    operatorValue: "contains",
                    value: debouncedValue
                }],
            })
        }
    }, [debouncedValue, filterPlaceholderField])

    return (
        <Box className='enterprise-table-wrapper'>
            <TextField
                variant="outlined"
                size="small"
                value={fastFilter}
                placeholder={getFilterInputPlaceholderByField(filterPlaceholderField, columns)}
                onChange={(event) => setFastFilter(event.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
                className="enterprise-filter-input"
            />
            {table}
            {showContactEnterpriseDialog &&
                <ContactEnterpriseDialog
                    open={showContactEnterpriseDialog}
                    handleClose={() => setShowContactEnterpriseDialog()}
                    createEnterprise={createEnterprise}
                    updateEnterprise={updateEnterprise}
                    selectedEnterprise={selectedEnterprise}
                    customFields={customFields}
                />
            }
            {showDeleteDialog &&
                <ConfirmationDialogCaptcha
                    open={showDeleteDialog}
                    title="Confirmação"
                    description={
                        <>
                            <Box>
                                Para excluir a empresa <span style={{ 'fontWeight': 'bold' }}>{selectedEnterprise.name}</span>, digite: <span style={{ 'fontWeight': 'bold' }}>excluir</span> no campo abaixo.
                            </Box>
                        </>
                    }
                    handleClose={() => setShowDeleteDialog(false)}
                    handleSubmit={() => deleteEnterprise(selectedEnterprise.id)}
                    captcha={'excluir'}
                />
            }
            {showMassDeleteEnterpriseDialog &&
                <ConfirmationDialogCaptcha
                    open={showMassDeleteEnterpriseDialog}
                    title="Confirmação"
                    description={
                        <>
                            <Box>
                                Para excluir as empresas selecionadas, digite: <span style={{ 'fontWeight': 'bold' }}>excluir</span> no campo abaixo.
                            </Box>
                        </>
                    }
                    handleClose={() => setShowMassDeleteEnterpriseDialog(false)}
                    handleSubmit={() => deleteEnterprises(selectedEnterprises)}
                    captcha={'excluir'}
                />
            }
            {showMassBulkEditEnterpriseDialog &&
                <BulkEditEnterpriseDialog
                    open={showMassBulkEditEnterpriseDialog}
                    handleClose={() => setShowMassBulkEditEnterpriseDialog(false)}
                    handleSubmit={bulkUpdateEnterprises}
                    customFields={customFields}
                />
            }
            {showMergeDialog &&
                <MergeEnterpriseDialog open={showMergeDialog}
                    enterprises={enterprises}
                    selectedEnterprises={selectedEnterprises}
                    customFields={customFields}
                    handleClose={() => setShowMergeDialog(false)}
                    handleSubmit={mergeSelectedEnterprises}
                />
            }
            {showAddEnterpriseGroup &&
                <AddEnterpriseGroupForm
                    selectionIds={[]}
                    enterpriseId={getSession().profile.enterprise.id}
                    open={showAddEnterpriseGroup}
                    handleClose={() => setShowAddEnterpriseGroup(false)}
                    submit={() => { }} // Already handling action within component
                    updateEnterprises={() => { }}
                    updateGroups={updateGroups}
                    subsObj={subsObj}
                    enterprises={enterprises}
                    updateForceReload={() => { }}
                    updateReady={() => { }}
                />
            }
            {showDeleteEnterpriseGroup &&
                <ConfirmationDialog open={showDeleteEnterpriseGroup}
                    title="Confirmação"
                    description="Tem certeza que deseja excluir o segmento selecionado?"
                    handleClose={() => setShowDeleteEnterpriseGroup(false)}
                    handleSubmit={deleteEnterpriseGroup}
                />
            }
            {showUpdateEnterpriseGroup &&
                <EditGroupNameModal
                    open={showUpdateEnterpriseGroup}
                    title="Editar Segmento"
                    buttonText="Salvar"
                    groups={groups}
                    handleClose={() => setShowUpdateEnterpriseGroup(false)}
                    submit={(data) => {
                        setEnterprises(prevState =>
                            prevState.map(each => ({
                                ...each,
                                groups: each.groups.map(group =>
                                    group.id === data.id ? { ...group, ...data } : group
                                )
                            }))
                        )
                        setShowUpdateEnterpriseGroup(false)
                    }}
                    enterpriseId={getSession().profile.enterprise.id}
                    selectedGroup={selectedEnterpriseGroup}
                />
            }
            {showGroupEnterprises &&
                <EditGroupEnterprisesModal
                    open={showGroupEnterprises}
                    title="Editar Segmento"
                    buttonText="Salvar"
                    groups={groups}
                    handleClose={() => setShowGroupEnterprises(false)}
                    submit={() => {
                        fetchEnterprises()
                        setShowGroupEnterprises(false)
                    }}
                    enterprises={enterprises}
                    enterpriseId={getSession().profile.enterprise.id}
                    selectedGroup={selectedEnterpriseGroup}
                    updateForceReload={() => { }}
                />
            }
            {deleteContactDialog &&
                <ConfirmationDialog open={deleteContactDialog}
                    title="Confirmação"
                    description={
                        selectedContact.name || selectedContact.telephone
                            ? `Tem certeza que deseja excluir o cliente ${
                                  selectedContact.name || selectedContact.telephone
                              }?`
                            : "Tem certeza que deseja excluir o cliente?"
                    }
                    handleClose={() => {
                        setSelectedContact(null)
                        showDeleteContactDialog(false)
                    }}
                    handleSubmit={() => {
                        deleteContact(selectedContact)
                        setSelectedContact(null)
                        showDeleteContactDialog(false)
                    }}
                />
            }

            {showCreateEvent &&
                <Event
                    open={showCreateEvent}
                    title="Criar evento"
                    handleClose={() => { setShowCreateEvent(false); setSelectedContactEnterprise(null) }}
                    handleSubmit={createEvent}
                    isSupervisor={userGroups.isSupervisor}
                    myTeam={myTeam}
                    selectedEvent={{ contact_enterprise_data: selectedContactEnterprise }}
                    setProfile={() => { }}
                    setupWhatsAppNotify={(_, set) => { setSingleClientWhatsAppNotify(selectedContactEnterprise.id); setSetup(p => set) }}
                    setupSMSNotify={(_, set) => { setSingleClientSMSNotify([selectedContactEnterprise.id]); setSetup(p => set) }}
                    setupEmailNotify={(_, set) => { setSingleClientEmailNotify(selectedContactEnterprise.id); setSetup(p => set) }}
                    lock
                />
            }
            {singleClientWhatsAppNotify &&
                <Templates
                    title={setup ? "Configurar notificação de whatsapp" : "Enviar whatsapp ao contato"}
                    open={singleClientWhatsAppNotify}
                    templates={templates}
                    queues={queues}
                    clients={enterprises.flatMap(enterprise => enterprise.contacts)}
                    selectionIds={[singleClientWhatsAppNotify]}
                    originNumbers={originNumbers}
                    handleClose={() => { setSingleClientWhatsAppNotify(null); setSetup(null) }}
                    handlePostSuccess={() => { }}
                    setup={setup}
                />
            }
            {deleteContactDialog &&
                <ConfirmationDialog open={deleteContactDialog}
                    title="Confirmação"
                    description={
                        selectedContact.name || selectedContact.telephone
                            ? `Tem certeza que deseja excluir o cliente ${
                                  selectedContact.name || selectedContact.telephone
                              }?`
                            : "Tem certeza que deseja excluir o cliente?"
                    }
                    handleClose={() => {
                        setSelectedContact(null)
                        showDeleteContactDialog(false)
                    }}
                    handleSubmit={() => {
                        deleteContact(selectedContact)
                        setSelectedContact(null)
                        showDeleteContactDialog(false)
                    }}
                />
            }
            {singleClientEmailNotify &&
                <SendEmail
                    title="Enviar e-mail ao contato"
                    queues={queues}
                    clients={[singleClientEmailNotify]}
                    handleClose={() => { setSingleClientEmailNotify(null); setSetup(null) }}
                    setup={setup}
                    canCopy={true}
                />
            }

            {singleClientSMSNotify &&
                <SendSMS
                    queues={queues}
                    selectedNumberModel={singleClientSMSNotify}
                    handleClose={() => { setSingleClientSMSNotify(null); setSetup(null) }}
                    title={setup ? "Configurar notificação de sms" : "Enviar SMS ao contato"}
                    setup={setup}
                />}

            {showCustomFields &&
                <CustomFieldsDialog 
                    open={showCustomFields}
                    customFields={customFields}
                    handleClose={() => setShowCustomFields(false)}
                    handleSubmit={submitCustomFields}
                />
            }

        </Box>
    )
}

export default EnterpriseTable
